import Phaser from 'phaser';
import type { EnemyKind, LevelDefinition, PlatformDefinition } from './types';

export const COLORS = {
  ink: 0x06111e,
  cream: 0xf6e5b6,
  gold: 0xd5a841,
  green: 0x64a64f,
  teal: 0x2ea6a0,
  burgundy: 0x8d3542,
  mustard: 0xd1942e,
  bubble: 0x8de8ee,
};

export function createGameTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists('frog-0-idle')) return;

  for (const slot of [0, 1] as const) {
    for (const pose of ['idle', 'run1', 'run2', 'jump', 'blow'] as const) {
      createFrogTexture(scene, slot, pose);
    }
  }

  const enemyColors: Record<EnemyKind, [number, number]> = {
    beetle: [0x4e8b55, 0xb6d06c],
    devil: [0xb94d4d, 0x6b243c],
    fountain: [0x55b8c5, 0xd1f4e7],
    bandit: [0x66466e, 0xcea765],
    yarn: [0xdf786a, 0xf7c68c],
    luggage: [0x9c6b3f, 0xe7b86b],
    wisp: [0x7fc3a6, 0xd7f3bb],
  };
  for (const [kind, colors] of Object.entries(enemyColors)) {
    createEnemyTexture(scene, kind as EnemyKind, colors);
  }

  createBubbleTexture(scene);
  createBossBubbleTexture(scene);
  createEnemyShotTexture(scene);
  createBossTexture(scene, 'stone-trickster', 0x756a73, 0xceaa75);
  createBossTexture(scene, 'mountain-wind', 0x477f8e, 0xc5e5c2);
  createSparkTexture(scene);
}

function createFrogTexture(
  scene: Phaser.Scene,
  slot: 0 | 1,
  pose: 'idle' | 'run1' | 'run2' | 'jump' | 'blow',
): void {
  const graphics = scene.add.graphics();
  const skin = slot === 0 ? COLORS.green : COLORS.teal;
  const darkSkin = slot === 0 ? 0x315f3f : 0x176b68;
  const lightSkin = slot === 0 ? 0xb9d88c : 0xa7ded0;
  const coat = slot === 0 ? COLORS.burgundy : COLORS.mustard;
  const runOffset = pose === 'run1' ? -2 : pose === 'run2' ? 2 : 0;
  const lift = pose === 'jump' ? -3 : 0;

  graphics.fillStyle(0x000000, 0.25);
  graphics.fillEllipse(22, 39, 34, 5);

  if (slot === 0) {
    graphics.lineStyle(2, 0x6c462b, 1);
    graphics.lineBetween(27, 14 + lift, 38, 33 + lift);
    graphics.fillStyle(0x9d6331, 1);
    graphics.fillEllipse(36, 29 + lift, 10, 14);
    graphics.lineStyle(1.5, 0xe4b55f, 1);
    graphics.strokeEllipse(36, 29 + lift, 7, 11);
    graphics.lineBetween(32, 17 + lift, 41, 8 + lift);
  }

  graphics.fillStyle(darkSkin, 1);
  graphics.fillEllipse(10 + runOffset, 30 + lift, 19, 13);
  graphics.fillEllipse(34 - runOffset, 30 + lift, 19, 13);
  graphics.fillStyle(skin, 1);
  graphics.fillEllipse(22, 26 + lift, 30, 21);
  graphics.fillEllipse(22, 17 + lift, 33, 22);
  graphics.fillStyle(coat, 1);
  graphics.fillRoundedRect(11, 23 + lift, 22, 8, 4);
  graphics.fillTriangle(11, 25 + lift, 7, 33 + lift, 16, 29 + lift);
  graphics.fillTriangle(33, 25 + lift, 37, 33 + lift, 28, 29 + lift);

  graphics.fillStyle(skin, 1);
  graphics.fillCircle(13, 8 + lift, 7);
  graphics.fillCircle(31, 8 + lift, 7);
  graphics.fillStyle(COLORS.cream, 1);
  graphics.fillEllipse(13, 8 + lift, 8, 6);
  graphics.fillEllipse(31, 8 + lift, 8, 6);
  graphics.fillStyle(COLORS.ink, 1);
  graphics.fillRoundedRect(10 + (pose === 'blow' ? 1 : 0), 7 + lift, 6, 2, 1);
  graphics.fillRoundedRect(28 + (pose === 'blow' ? 1 : 0), 7 + lift, 6, 2, 1);
  graphics.fillStyle(lightSkin, 1);
  graphics.fillEllipse(22, 18 + lift, pose === 'blow' ? 18 : 16, pose === 'blow' ? 12 : 8);
  graphics.lineStyle(1.5, darkSkin, 1);
  graphics.beginPath();
  graphics.arc(22, 17 + lift, 7, 0.32, 2.82);
  graphics.strokePath();

  graphics.fillStyle(darkSkin, 0.72);
  for (const [x, y, radius] of [[8, 20, 2], [17, 14, 1.5], [29, 20, 1.7], [35, 17, 1.3]] as const) {
    graphics.fillCircle(x, y + lift, radius);
  }

  graphics.lineStyle(4, skin, 1);
  graphics.lineBetween(14, 26 + lift, 8 + runOffset, 36 + lift);
  graphics.lineBetween(30, 26 + lift, 36 - runOffset, 36 + lift);
  graphics.lineStyle(2, skin, 1);
  for (const toe of [-3, 0, 3]) {
    graphics.lineBetween(8 + runOffset, 36 + lift, 8 + runOffset + toe, 39 + lift);
    graphics.lineBetween(36 - runOffset, 36 + lift, 36 - runOffset + toe, 39 + lift);
  }

  if (slot === 1) {
    graphics.lineStyle(2.5, 0x78472e, 1);
    graphics.beginPath();
    graphics.arc(34, 17 + lift, 7, 2.8, 5.6);
    graphics.strokePath();
    graphics.fillStyle(0xc78b44, 1);
    graphics.fillCircle(39, 12 + lift, 2.2);
    if (pose === 'blow') {
      graphics.fillStyle(COLORS.bubble, 0.34);
      graphics.fillCircle(42, 9 + lift, 4);
      graphics.lineStyle(1.5, 0xd8ffff, 0.9);
      graphics.strokeCircle(42, 9 + lift, 3.5);
    }
  }
  graphics.generateTexture(`frog-${slot}-${pose}`, 44, 42);
  graphics.destroy();
}

function createEnemyTexture(scene: Phaser.Scene, kind: EnemyKind, colors: [number, number]): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(0x000000, 0.25);
  graphics.fillEllipse(17, 32, 24, 4);
  graphics.fillStyle(colors[0], 1);

  if (kind === 'beetle') {
    graphics.fillEllipse(17, 18, 22, 25);
    graphics.fillStyle(colors[1], 0.65);
    graphics.fillEllipse(13, 17, 8, 18);
    graphics.fillEllipse(21, 17, 8, 18);
    graphics.lineStyle(2, COLORS.ink, 0.9);
    graphics.lineBetween(17, 6, 17, 29);
    for (const y of [11, 18, 25]) {
      graphics.lineBetween(8, y, 2, y - 4);
      graphics.lineBetween(26, y, 32, y - 4);
    }
    graphics.lineBetween(12, 7, 8, 2);
    graphics.lineBetween(22, 7, 26, 2);
  } else if (kind === 'devil') {
    graphics.fillRoundedRect(6, 9, 22, 22, 8);
    graphics.fillTriangle(8, 11, 5, 1, 15, 8);
    graphics.fillTriangle(26, 11, 29, 1, 19, 8);
    graphics.fillStyle(colors[1], 1);
    graphics.fillTriangle(11, 28, 17, 34, 22, 28);
    graphics.lineStyle(2.5, colors[0], 1);
    graphics.beginPath();
    graphics.arc(27, 22, 8, -1.2, 1.25);
    graphics.strokePath();
    graphics.fillTriangle(31, 28, 33, 22, 27, 24);
  } else if (kind === 'fountain') {
    graphics.fillEllipse(17, 19, 22, 24);
    graphics.fillTriangle(6, 25, 17, 33, 28, 25);
    graphics.fillStyle(colors[1], 1);
    graphics.fillCircle(17, 5, 5);
    graphics.fillCircle(8, 10, 3);
    graphics.fillCircle(26, 10, 3);
    graphics.lineStyle(1.5, 0xffffff, 0.75);
    graphics.beginPath();
    graphics.arc(17, 18, 8, 3.6, 5.4);
    graphics.strokePath();
  } else if (kind === 'bandit') {
    graphics.fillRoundedRect(6, 8, 22, 24, 7);
    graphics.fillTriangle(6, 12, 17, 2, 28, 12);
    graphics.fillStyle(COLORS.ink, 1);
    graphics.fillRoundedRect(6, 12, 22, 7, 3);
    graphics.fillStyle(colors[1], 1);
    graphics.fillCircle(12, 15, 1.5);
    graphics.fillCircle(22, 15, 1.5);
    graphics.fillStyle(0x9b7241, 1);
    graphics.fillCircle(28, 26, 6);
  } else if (kind === 'yarn') {
    graphics.fillCircle(17, 18, 14);
    graphics.lineStyle(2, colors[1], 1);
    graphics.strokeCircle(17, 18, 9);
    graphics.lineBetween(5, 20, 29, 13);
    graphics.lineBetween(9, 7, 25, 29);
    graphics.lineBetween(5, 14, 29, 23);
    graphics.lineStyle(1.5, 0xe8ded0, 1);
    graphics.lineBetween(23, 3, 12, 31);
    graphics.lineBetween(29, 6, 9, 27);
  } else if (kind === 'luggage') {
    graphics.fillRoundedRect(4, 9, 26, 21, 5);
    graphics.lineStyle(2, colors[1], 1);
    graphics.strokeRoundedRect(11, 3, 12, 9, 3);
    graphics.lineBetween(10, 11, 10, 28);
    graphics.lineBetween(24, 11, 24, 28);
    graphics.fillStyle(COLORS.ink, 1);
    graphics.fillCircle(9, 31, 2);
    graphics.fillCircle(25, 31, 2);
  } else {
    graphics.fillCircle(17, 15, 13);
    graphics.fillTriangle(5, 20, 11, 33, 17, 24);
    graphics.fillTriangle(17, 24, 23, 33, 30, 18);
    graphics.fillStyle(colors[1], 0.65);
    graphics.fillCircle(12, 10, 5);
  }

  graphics.fillStyle(COLORS.ink, 1);
  graphics.fillCircle(12, 16, 2);
  graphics.fillCircle(22, 16, 2);
  graphics.fillStyle(0xffffff, 0.82);
  graphics.fillCircle(11.5, 15.5, 0.7);
  graphics.fillCircle(21.5, 15.5, 0.7);
  graphics.generateTexture(`enemy-${kind}`, 34, 34);
  graphics.destroy();
}

function createBubbleTexture(scene: Phaser.Scene): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(0x5bd4e6, 0.2);
  graphics.fillCircle(12, 12, 11);
  graphics.lineStyle(1.8, COLORS.bubble, 0.9);
  graphics.strokeCircle(12, 12, 10);
  graphics.lineStyle(2, 0xffffff, 0.85);
  graphics.beginPath();
  graphics.arc(11, 11, 6.5, 3.5, 5.1);
  graphics.strokePath();
  graphics.fillStyle(0xe8b7ef, 0.6);
  graphics.fillCircle(18, 17, 1.5);
  graphics.generateTexture('bubble', 24, 24);
  graphics.destroy();
}

function createBossBubbleTexture(scene: Phaser.Scene): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(0x66dce6, 0.2);
  graphics.fillCircle(35, 35, 33);
  graphics.lineStyle(3, 0xaef7f5, 0.96);
  graphics.strokeCircle(35, 35, 31);
  graphics.lineStyle(5, 0xffffff, 0.75);
  graphics.beginPath();
  graphics.arc(31, 30, 22, 3.55, 4.78);
  graphics.strokePath();
  for (const [x, y, color] of [[15, 52, 0xf5b7e7], [54, 17, 0xf5d66f], [51, 53, 0x9bd9ff]] as const) {
    graphics.fillStyle(color, 0.75);
    graphics.fillCircle(x, y, 3);
  }
  graphics.generateTexture('boss-bubble', 70, 70);
  graphics.destroy();
}

function createEnemyShotTexture(scene: Phaser.Scene): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(0x70dbe9, 0.75);
  graphics.fillCircle(7, 7, 6);
  graphics.lineStyle(1.5, 0xe5ffff, 0.9);
  graphics.strokeCircle(7, 7, 5);
  graphics.fillStyle(0xffffff, 0.9);
  graphics.fillCircle(5, 4, 1.5);
  graphics.generateTexture('enemy-shot', 14, 14);
  graphics.destroy();
}

function createBossTexture(scene: Phaser.Scene, key: string, base: number, accent: number): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(0x000000, 0.28);
  graphics.fillEllipse(30, 57, 45, 7);
  if (key === 'stone-trickster') {
    graphics.fillStyle(base, 1);
    graphics.fillRoundedRect(7, 12, 46, 43, 13);
    graphics.fillTriangle(9, 17, 5, 2, 22, 12);
    graphics.fillTriangle(51, 17, 55, 2, 38, 12);
    graphics.fillStyle(accent, 1);
    graphics.fillEllipse(30, 39, 28, 17);
    graphics.fillStyle(0x52656a, 1);
    graphics.fillCircle(18, 25, 6);
    graphics.fillCircle(42, 25, 6);
    graphics.fillStyle(COLORS.ink, 1);
    graphics.fillCircle(18, 25, 3);
    graphics.fillCircle(42, 25, 3);
    graphics.lineStyle(3, COLORS.ink, 1);
    graphics.beginPath();
    graphics.arc(30, 40, 10, 0.3, 2.84);
    graphics.strokePath();
    graphics.lineStyle(2, 0xd8ccaa, 0.6);
    for (const x of [13, 24, 36, 47]) graphics.lineBetween(x, 13, x - 3, 8);
  } else {
    graphics.fillStyle(base, 0.9);
    graphics.fillCircle(30, 29, 24);
    graphics.fillStyle(accent, 0.75);
    graphics.fillCircle(30, 29, 16);
    graphics.fillStyle(0x2e6374, 1);
    graphics.fillCircle(30, 29, 9);
    graphics.lineStyle(5, accent, 0.85);
    for (let radius = 15; radius <= 26; radius += 6) {
      graphics.beginPath();
      graphics.arc(30, 29, radius, -0.9 + radius * 0.04, 3.7 + radius * 0.02);
      graphics.strokePath();
    }
    graphics.fillStyle(COLORS.ink, 1);
    graphics.fillCircle(22, 25, 3);
    graphics.fillCircle(38, 25, 3);
    graphics.lineStyle(2, COLORS.ink, 1);
    graphics.lineBetween(22, 39, 38, 39);
  }
  graphics.generateTexture(`boss-${key}`, 60, 60);
  graphics.destroy();
}

function createSparkTexture(scene: Phaser.Scene): void {
  const graphics = scene.add.graphics();
  graphics.fillStyle(COLORS.cream, 1);
  graphics.fillCircle(5, 5, 2.5);
  graphics.fillTriangle(5, 0, 3.5, 5, 6.5, 5);
  graphics.fillTriangle(5, 10, 3.5, 5, 6.5, 5);
  graphics.fillTriangle(0, 5, 5, 3.5, 5, 6.5);
  graphics.fillTriangle(10, 5, 5, 3.5, 5, 6.5);
  graphics.generateTexture('spark', 10, 10);
  graphics.destroy();
}

export function drawLevelBackground(scene: Phaser.Scene, level: LevelDefinition): void {
  const generatedKey = `level-bg-${level.id}`;
  if (scene.textures.exists(generatedKey)) {
    scene.add.image(240, 135, generatedKey).setDisplaySize(480, 270).setDepth(-40);
    scene.add.rectangle(240, 135, 480, 270, 0x04101c, 0.13).setDepth(-39);
    return;
  }
  const graphics = scene.add.graphics().setDepth(-40);
  drawSky(graphics, level);
  const themeDrawers: Record<LevelDefinition['theme'], (g: Phaser.GameObjects.Graphics, level: LevelDefinition) => void> = {
    'frog-house': drawFrogHouse,
    'wojska-square': drawSquare,
    market: drawMarket,
    castle: drawCastle,
    factory: drawFactory,
    station: drawStation,
    gondola: drawGondola,
    summit: drawSummit,
    library: drawStation,
    blonia: drawGondola,
    sfera: drawFactory,
  };
  themeDrawers[level.theme](graphics, level);
}

function drawSky(g: Phaser.GameObjects.Graphics, level: LevelDefinition): void {
  const [sky, mid] = level.palette;
  g.fillStyle(sky, 1);
  g.fillRect(0, 0, 480, 270);
  g.fillStyle(mid, 0.18);
  g.fillEllipse(240, 156, 620, 245);
  g.fillStyle(level.palette[3], 0.055);
  g.fillEllipse(110, 88, 260, 118);
  let seed = level.id * 7_919;
  for (let i = 0; i < 28; i += 1) {
    seed = (seed * 16_807) % 2_147_483_647;
    const x = seed % 480;
    seed = (seed * 16_807) % 2_147_483_647;
    const y = 25 + (seed % 95);
    g.fillStyle(i % 5 === 0 ? level.palette[3] : 0xf6edc7, 0.4 + (i % 3) * 0.18);
    g.fillCircle(x, y, i % 4 === 0 ? 1.3 : 0.7);
  }
}

function drawMountains(g: Phaser.GameObjects.Graphics, color: number, y: number, offset: number): void {
  g.fillStyle(color, 1);
  g.beginPath();
  g.moveTo(0, 270);
  for (let x = -50; x <= 520; x += 52) {
    g.lineTo(x, y + ((x / 52 + offset) % 2) * 12);
    g.lineTo(x + 26, y - 22 - ((x / 52 + offset) % 3) * 6);
  }
  g.lineTo(520, 270);
  g.closePath();
  g.fillPath();
}

function drawLitWindow(g: Phaser.GameObjects.Graphics, x: number, y: number, width: number, height: number): void {
  g.fillStyle(0x17354a, 1);
  g.fillRoundedRect(x, y, width, height, Math.min(width, height) * 0.28);
  g.fillStyle(0xf0ba55, 0.78);
  g.fillRoundedRect(x + 3, y + height * 0.43, width - 6, height * 0.48, 2);
  g.lineStyle(1.2, 0x31584f, 1);
  g.lineBetween(x + width / 2, y + 2, x + width / 2, y + height - 2);
  g.lineBetween(x + 2, y + height * 0.55, x + width - 2, y + height * 0.55);
}

function drawOrnament(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(0x2f6957, 1);
  g.fillEllipse(x, y, 9, 13);
  g.lineStyle(1.5, 0x193f38, 1);
  g.lineBetween(x, y - 6, x, y + 6);
  for (const side of [-1, 1]) {
    g.lineBetween(x + side * 3, y - 3, x + side * 8, y - 7);
    g.lineBetween(x + side * 4, y + 1, x + side * 9, y + 1);
    g.lineBetween(x + side * 3, y + 4, x + side * 8, y + 8);
  }
}

function drawFrogHouse(g: Phaser.GameObjects.Graphics, level: LevelDefinition): void {
  drawMountains(g, 0x102d43, 150, 1);
  g.fillStyle(0x23463f, 1);
  g.fillRoundedRect(28, 70, 424, 184, 8);
  g.fillStyle(0xe3d3aa, 1);
  g.fillRoundedRect(35, 76, 410, 178, 7);

  // Asymetryczna narożna wieża i falujące secesyjne szczyty.
  g.fillStyle(0xd9c79d, 1);
  g.fillRoundedRect(48, 38, 96, 216, 36);
  g.fillStyle(0x315b53, 1);
  g.fillEllipse(96, 48, 106, 54);
  g.fillTriangle(48, 54, 96, 13, 144, 54);
  g.fillStyle(0xe3d3aa, 1);
  g.fillEllipse(96, 57, 89, 54);
  g.fillTriangle(56, 52, 96, 22, 136, 52);
  g.fillStyle(0x315b53, 1);
  g.fillCircle(96, 24, 5);

  g.fillStyle(0xe3d3aa, 1);
  g.fillEllipse(266, 72, 208, 76);
  g.fillEllipse(389, 80, 112, 58);
  g.fillRect(164, 72, 281, 182);
  g.lineStyle(3, 0x3b675b, 1);
  g.beginPath();
  g.arc(266, 72, 102, 3.22, 6.2);
  g.strokePath();
  g.beginPath();
  g.arc(389, 80, 53, 3.2, 6.12);
  g.strokePath();

  for (const [x, y] of [[68, 78], [112, 78], [190, 100], [232, 91], [280, 91], [329, 99], [380, 106], [418, 106]] as const) {
    drawLitWindow(g, x - 11, y, 22, x < 150 ? 40 : 34);
  }
  for (const [x, y] of [[72, 151], [120, 151], [185, 151], [295, 151], [360, 151], [414, 151]] as const) {
    drawLitWindow(g, x - 13, y, 26, 41);
  }

  // Charakterystyczne nerkowate witryny i zielony portal z dwiema żabami.
  g.fillStyle(0x315b53, 1);
  g.fillEllipse(171, 224, 73, 53);
  g.fillEllipse(360, 224, 82, 55);
  g.fillStyle(0xe8b654, 0.68);
  g.fillEllipse(171, 224, 59, 41);
  g.fillEllipse(360, 224, 68, 42);
  g.fillStyle(0x244a43, 1);
  g.fillRoundedRect(221, 192, 58, 62, 24);
  g.lineStyle(2, 0x91aa7d, 1);
  g.strokeRoundedRect(225, 196, 50, 58, 20);

  for (const x of [238, 262]) {
    g.fillStyle(0x597f4d, 1);
    g.fillEllipse(x, 188, 17, 11);
    g.fillCircle(x - 4, 183, 4);
    g.fillCircle(x + 4, 183, 4);
    g.lineStyle(2, 0x31543b, 1);
    g.lineBetween(x - 6, 191, x - 10, 197);
    g.lineBetween(x + 6, 191, x + 10, 197);
  }
  drawOrnament(g, 181, 73);
  drawOrnament(g, 342, 70);
  drawOrnament(g, 417, 88);

  g.lineStyle(2, 0x6b9277, 0.75);
  for (const x of [155, 207, 319, 398]) {
    g.beginPath();
    g.arc(x, 123, 18, 0.1, 2.8);
    g.strokePath();
  }
  g.fillStyle(level.palette[1], 0.55);
  g.fillEllipse(240, 258, 470, 28);
}

function drawSquare(g: Phaser.GameObjects.Graphics, level: LevelDefinition): void {
  drawMountains(g, 0x18364a, 152, 2);
  const colors = [0xe3d3aa, 0xc79072, 0xe0c69f, 0xa7b99d, 0xd5af72];
  for (let i = 0; i < 5; i += 1) {
    const x = -8 + i * 101;
    const roofY = 83 - (i % 2) * 10;
    g.fillStyle(0x333e46, 1);
    g.fillTriangle(x, roofY, x + 50, roofY - 28, x + 101, roofY);
    g.fillStyle(colors[i], 1);
    g.fillRoundedRect(x, roofY, 101, 136, 4);
    for (let row = 0; row < 2; row += 1) {
      drawLitWindow(g, x + 15, 100 + row * 47, 22, 30);
      drawLitWindow(g, x + 63, 100 + row * 47, 22, 30);
    }
  }
  // Smukła wieża kościoła Marcina Lutra zamyka perspektywę placu.
  g.fillStyle(0xb9ab87, 1);
  g.fillRect(421, 40, 38, 177);
  g.fillStyle(0x31505a, 1);
  g.fillTriangle(414, 42, 440, 7, 466, 42);
  g.fillCircle(440, 73, 11);
  g.fillStyle(0xe5c76e, 1);
  g.fillCircle(440, 73, 7);
  g.lineStyle(1.5, 0x3a4e51, 1);
  g.lineBetween(440, 73, 440, 67);
  g.lineBetween(440, 73, 445, 76);

  // Jasna, piesza nawierzchnia i współczesna fontanna z żabami.
  g.fillStyle(0xb9ad91, 1);
  g.beginPath();
  g.moveTo(0, 210);
  g.lineTo(480, 202);
  g.lineTo(480, 270);
  g.lineTo(0, 270);
  g.closePath();
  g.fillPath();
  g.lineStyle(1, 0xe0d4b6, 0.5);
  for (let y = 218; y < 270; y += 12) g.lineBetween(0, y, 480, y - 6);
  for (let x = 10; x < 480; x += 32) g.lineBetween(x, 208, x + 14, 270);
  g.fillStyle(0x456f70, 1);
  g.fillEllipse(255, 235, 126, 26);
  g.fillStyle(0x76cbd0, 0.55);
  g.fillEllipse(255, 231, 110, 17);
  for (const x of [223, 287]) {
    g.fillStyle(0x406d50, 1);
    g.fillEllipse(x, 220, 18, 10);
    g.fillCircle(x - 4, 215, 4);
    g.fillCircle(x + 4, 215, 4);
    g.lineStyle(1.5, 0x8fe0df, 0.75);
    g.beginPath();
    g.arc(x, 208, 14, 3.4, 6.05);
    g.strokePath();
  }
  g.fillStyle(level.palette[3], 0.75);
  g.fillRoundedRect(46, 198, 88, 10, 4);
  g.fillRoundedRect(338, 191, 91, 10, 4);
}

function drawMarket(g: Phaser.GameObjects.Graphics, level: LevelDefinition): void {
  drawMountains(g, 0x1b4152, 160, 1);
  const houses = [0xd7a78c, 0xe0c7a2, 0x9bb6a4, 0xd5b274, 0xb78992, 0xe3cfaa];
  for (let index = 0; index < houses.length; index += 1) {
    const x = -12 + index * 84;
    const roof = 74 + (index % 3) * 8;
    g.fillStyle(0x3b4850, 1);
    g.fillTriangle(x, roof, x + 42, roof - 22, x + 84, roof);
    g.fillStyle(houses[index], 1);
    g.fillRect(x, roof, 84, 151);
    drawLitWindow(g, x + 12, roof + 22, 20, 29);
    drawLitWindow(g, x + 52, roof + 22, 20, 29);
    drawLitWindow(g, x + 12, roof + 67, 20, 29);
    drawLitWindow(g, x + 52, roof + 67, 20, 29);
    g.fillStyle(0x4c514b, 1);
    g.fillRoundedRect(x + 6, roof + 118, 31, 34, 16);
    g.fillRoundedRect(x + 47, roof + 118, 31, 34, 16);
  }
  g.fillStyle(0x756d62, 1);
  g.fillRect(0, 222, 480, 48);
  g.lineStyle(1, 0xb7a995, 0.55);
  for (let x = -20; x < 500; x += 28) g.lineBetween(x, 222, x + 44, 270);

  // Odkryty kanał dawnej ulicy biegnący od fontanny przez Rynek.
  g.fillStyle(0x335e68, 1);
  g.beginPath();
  g.moveTo(224, 210);
  g.lineTo(258, 210);
  g.lineTo(322, 270);
  g.lineTo(156, 270);
  g.closePath();
  g.fillPath();
  g.fillStyle(0x70c4cc, 0.5);
  g.beginPath();
  g.moveTo(232, 211);
  g.lineTo(251, 211);
  g.lineTo(292, 270);
  g.lineTo(187, 270);
  g.closePath();
  g.fillPath();
  g.fillStyle(0xb6aa90, 1);
  g.fillRoundedRect(196, 238, 90, 8, 4);

  // Fontanna Neptuna: misa, postać i trójząb.
  g.fillStyle(0x3c6570, 1);
  g.fillEllipse(241, 216, 99, 26);
  g.fillStyle(0x83b8ad, 1);
  g.fillRoundedRect(232, 167, 18, 47, 7);
  g.fillCircle(241, 156, 12);
  g.fillTriangle(230, 172, 241, 188, 252, 172);
  g.lineStyle(3, 0xd9c48d, 1);
  g.lineBetween(256, 151, 256, 196);
  g.lineBetween(248, 156, 256, 150);
  g.lineBetween(264, 156, 256, 150);
  g.lineBetween(256, 150, 256, 143);
  g.lineStyle(2, 0x7bd6dd, 0.72);
  for (const x of [211, 221, 261, 271]) g.lineBetween(241, 183, x, 211);
  g.fillStyle(level.palette[3], 0.6);
  g.fillCircle(47, 229, 7);
}

function drawCastle(g: Phaser.GameObjects.Graphics): void {
  drawMountains(g, 0x112a3d, 160, 0);
  g.fillStyle(0x7e4237, 1);
  g.fillRect(35, 75, 410, 181);
  g.fillStyle(0xd6cfb4, 1);
  g.fillRect(41, 81, 398, 175);
  g.fillRoundedRect(58, 42, 82, 214, 5);
  g.fillRoundedRect(348, 58, 70, 198, 5);
  g.fillStyle(0x784239, 1);
  g.fillTriangle(52, 44, 99, 17, 146, 44);
  g.fillTriangle(342, 60, 383, 35, 424, 60);
  g.fillRect(41, 83, 398, 8);
  for (let x = 45; x < 435; x += 28) {
    g.fillStyle(0xd6cfb4, 1);
    g.fillRect(x, 68, 17, 20);
    g.fillStyle(0x7e4237, 1);
    g.fillRect(x + 3, 71, 11, 4);
  }
  g.fillStyle(0x344550, 1);
  g.fillRoundedRect(205, 164, 70, 92, 31);
  g.lineStyle(3, 0x8d5547, 1);
  g.strokeRoundedRect(209, 168, 62, 88, 28);
  for (const [x, y] of [[86, 76], [116, 76], [172, 112], [308, 112], [370, 91], [398, 91]] as const) {
    drawLitWindow(g, x - 9, y, 18, 34);
  }
  // Herby i czerwone ceglane opaski są znakiem rozpoznawczym elewacji.
  g.fillStyle(0x8b4d40, 1);
  for (const x of [159, 321]) {
    g.fillRoundedRect(x - 16, 93, 32, 38, 14);
    g.fillStyle(0xd8bb62, 1);
    g.fillCircle(x, 108, 7);
    g.fillStyle(0x8b4d40, 1);
  }
  // Niewielka nisza z satyrem przy murze zapowiada Kamiennego Psotnika.
  g.fillStyle(0x8a806e, 1);
  g.fillRoundedRect(290, 188, 42, 61, 19);
  g.fillStyle(0x52605c, 1);
  g.fillCircle(311, 207, 9);
  g.fillTriangle(303, 201, 300, 191, 309, 198);
  g.fillTriangle(319, 201, 322, 191, 313, 198);
  g.fillEllipse(311, 229, 17, 25);
  g.lineStyle(2, 0x6ab9c0, 0.75);
  g.lineBetween(311, 218, 311, 244);
}

function drawFactory(g: Phaser.GameObjects.Graphics): void {
  g.fillStyle(0x4c352f, 1);
  g.fillRect(0, 66, 480, 190);
  // Zębate świetliki hal włókienniczych.
  for (let x = 0; x < 480; x += 80) {
    g.fillStyle(0x68483a, 1);
    g.fillTriangle(x, 67, x + 42, 34, x + 80, 67);
    g.fillStyle(0x263d49, 1);
    g.fillTriangle(x + 10, 63, x + 42, 40, x + 68, 63);
  }
  g.lineStyle(1, 0x8c5c47, 0.65);
  for (let y = 76; y < 256; y += 12) g.lineBetween(0, y, 480, y);
  for (let x = 0; x < 480; x += 25) g.lineBetween(x, 66, x + ((x / 25) % 2 ? 12 : -12), 256);

  for (const x of [25, 111, 369, 431]) {
    g.fillStyle(0x233640, 1);
    g.fillRoundedRect(x, 88, 54, 68, 14);
    g.fillStyle(0x6f9aa0, 0.4);
    g.fillRoundedRect(x + 6, 95, 42, 54, 9);
    g.lineStyle(2, 0xa8744d, 1);
    g.lineBetween(x + 27, 94, x + 27, 150);
    g.lineBetween(x + 5, 122, x + 49, 122);
  }

  // Krosna, szpule, pasy transmisyjne i mosiężne koła.
  g.fillStyle(0x283b42, 1);
  g.fillRoundedRect(133, 139, 214, 93, 8);
  g.lineStyle(5, 0xa3774b, 1);
  g.strokeRoundedRect(144, 151, 191, 68, 5);
  for (const x of [166, 211, 256, 301]) {
    g.strokeCircle(x, 184, 16);
    g.lineBetween(x - 16, 184, x + 16, 184);
    g.lineBetween(x, 168, x, 200);
  }
  g.lineStyle(4, 0x202e33, 1);
  g.lineBetween(0, 59, 480, 59);
  g.lineBetween(53, 25, 53, 248);
  g.lineBetween(402, 31, 402, 248);
  g.lineStyle(3, 0xd28c68, 0.85);
  for (let x = 151; x <= 330; x += 9) g.lineBetween(x, 155, x - 4, 216);
  g.fillStyle(0xd8a45f, 1);
  for (const [x, color] of [[72, 0xdd6e61], [104, 0xe7bf70], [374, 0x6eaaa1]] as const) {
    g.fillStyle(color, 1);
    g.fillEllipse(x, 224, 23, 33);
    g.lineStyle(2, 0xf4d7a2, 0.8);
    g.lineBetween(x - 10, 224, x + 10, 224);
  }
}

function drawStation(g: Phaser.GameObjects.Graphics, level: LevelDefinition): void {
  g.fillStyle(0xd3bd91, 1);
  g.fillRect(0, 58, 480, 196);
  g.fillStyle(0x8e4439, 1);
  g.fillRect(0, 66, 480, 9);
  g.fillRect(0, 116, 480, 7);
  // Pompejańskie pasy i medaliony zachowane w hali dworca.
  for (let x = 20; x < 480; x += 54) {
    g.fillStyle(x % 108 === 20 ? 0x496f68 : 0xa95242, 1);
    g.fillCircle(x, 95, 11);
    g.fillStyle(0xe5cc95, 1);
    g.fillCircle(x, 95, 6);
  }
  g.fillStyle(0x5f5551, 1);
  g.fillRoundedRect(58, 48, 364, 199, 89);
  g.fillStyle(0x263b4e, 1);
  g.fillRoundedRect(67, 57, 346, 190, 82);
  g.fillStyle(0x8ab0b1, 0.26);
  g.fillRoundedRect(76, 66, 328, 178, 74);
  g.lineStyle(3, 0xa95846, 1);
  for (let x = 94; x < 405; x += 38) g.lineBetween(240, 54, x, 244);
  g.lineBetween(67, 147, 413, 147);
  g.lineBetween(72, 195, 408, 195);
  g.fillStyle(0xe1bd65, 1);
  g.fillCircle(240, 89, 23);
  g.fillStyle(0x283c4d, 1);
  g.fillCircle(240, 89, 17);
  g.lineStyle(2, 0xf0dfae, 1);
  g.lineBetween(240, 89, 240, 77);
  g.lineBetween(240, 89, 250, 94);
  g.fillStyle(0x9b4c3f, 1);
  g.fillRoundedRect(154, 124, 172, 27, 9);
  g.fillStyle(0xf1d7a2, 1);
  g.fillRoundedRect(162, 131, 156, 12, 5);
  // Peron i sylwetka historycznego pociągu.
  g.fillStyle(0x252b31, 1);
  g.fillRoundedRect(20, 213, 440, 35, 7);
  g.fillStyle(0x3f6d66, 1);
  g.fillRoundedRect(39, 198, 318, 35, 8);
  g.fillStyle(level.palette[3], 1);
  g.fillRoundedRect(357, 188, 76, 45, 13);
  for (const x of [58, 96, 134, 172, 210, 248, 286, 375, 405]) {
    g.fillStyle(0xe7bd63, 0.75);
    g.fillRoundedRect(x, 204, 20, 13, 4);
  }
  g.fillStyle(0x171e24, 1);
  for (const x of [68, 148, 228, 330, 397]) g.fillCircle(x, 237, 11);
  g.lineStyle(2, 0x8c7560, 1);
  g.lineBetween(0, 253, 480, 253);
  g.lineBetween(0, 262, 480, 262);
}

function drawGondola(g: Phaser.GameObjects.Graphics): void {
  // Panorama Bielska-Białej u podnóża Szyndzielni.
  g.fillStyle(0xd5bd68, 0.4);
  for (let x = 12; x < 470; x += 17) g.fillCircle(x, 172 + (x % 5) * 3, 1.2);
  drawMountains(g, 0x244f48, 159, 2);
  drawMountains(g, 0x183e3e, 187, 0);
  // Stalowe podpory i charakterystyczne żółte gondole.
  g.lineStyle(4, 0x829094, 1);
  g.lineBetween(-10, 42, 490, 118);
  g.lineBetween(-10, 48, 490, 124);
  for (const [x, top] of [[176, 70], [362, 99]] as const) {
    g.lineStyle(5, 0x69787d, 1);
    g.lineBetween(x - 22, 232, x, top);
    g.lineBetween(x + 22, 232, x, top);
    g.lineBetween(x - 26, top + 2, x + 26, top + 2);
    g.lineStyle(2, 0xaab4b4, 1);
    g.lineBetween(x - 19, 165, x + 19, 165);
  }
  for (const [x, y] of [[78, 55], [274, 85], [432, 109]] as const) {
    g.lineStyle(2, 0x8b9698, 1);
    g.lineBetween(x, y, x, y + 20);
    g.fillStyle(0xe3b62f, 1);
    g.fillRoundedRect(x - 21, y + 18, 42, 31, 8);
    g.fillStyle(0x294b5b, 1);
    g.fillRoundedRect(x - 16, y + 22, 32, 16, 5);
    g.lineStyle(1.5, 0xf3db74, 1);
    g.strokeRoundedRect(x - 21, y + 18, 42, 31, 8);
  }
  for (let x = 0; x < 480; x += 18) {
    g.fillStyle(x % 36 ? 0x1f5540 : 0x2d6a4b, 1);
    g.fillTriangle(x, 258, x + 10, 172 - (x % 4) * 9, x + 20, 258);
  }
  g.fillStyle(0x3e554d, 1);
  g.fillRoundedRect(377, 198, 89, 55, 7);
  g.fillStyle(0x73533b, 1);
  g.fillTriangle(368, 200, 421, 172, 474, 200);
  g.fillStyle(0xd9b95e, 0.7);
  g.fillRoundedRect(398, 216, 20, 20, 4);
  g.fillRoundedRect(432, 216, 20, 20, 4);
}

function drawSummit(g: Phaser.GameObjects.Graphics): void {
  g.fillStyle(0xe9d985, 0.9);
  g.fillCircle(401, 54, 24);
  g.fillStyle(0x10253f, 1);
  g.fillCircle(410, 48, 24);
  drawMountains(g, 0x1b3a4d, 172, 1);
  drawMountains(g, 0x173743, 202, 0);
  // Schronisko PTTK z szerokim dachem i ciepłymi oknami.
  g.fillStyle(0x405249, 1);
  g.fillRoundedRect(32, 159, 145, 94, 6);
  g.fillStyle(0x744f3d, 1);
  g.fillTriangle(17, 162, 103, 116, 192, 162);
  g.fillTriangle(80, 135, 142, 104, 184, 156);
  for (const [x, y] of [[51, 184], [83, 184], [123, 176], [151, 176]] as const) {
    g.fillStyle(0xdeb759, 0.82);
    g.fillRoundedRect(x, y, 20, 22, 4);
    g.lineStyle(1.5, 0x2d4b45, 1);
    g.lineBetween(x + 10, y, x + 10, y + 22);
  }
  // Wieża widokowa z krzyżulcami, platformą i światłem na szczycie.
  g.lineStyle(4, 0x778784, 1);
  g.lineBetween(306, 247, 336, 69);
  g.lineBetween(366, 247, 336, 69);
  for (let y = 91; y < 235; y += 29) {
    const spread = (y - 69) * 0.18;
    g.lineBetween(336 - spread, y, 336 + spread, y);
    g.lineBetween(336 - spread, y, 336 + spread + 5, y + 29);
    g.lineBetween(336 + spread, y, 336 - spread - 5, y + 29);
  }
  g.lineStyle(5, 0x899895, 1);
  g.lineBetween(303, 92, 369, 92);
  g.lineBetween(315, 70, 357, 70);
  g.fillStyle(0xe8d16e, 0.9);
  g.fillCircle(336, 64, 4);
  // Światła Bielska-Białej pod górami.
  for (let x = 8; x < 480; x += 13) {
    g.fillStyle(x % 39 === 8 ? 0xf1d077 : 0xc8a95a, 0.45);
    g.fillCircle(x, 238 + (x % 4) * 4, 1.1);
  }
}

export function drawPlatform(scene: Phaser.Scene, platform: PlatformDefinition): Phaser.GameObjects.Rectangle {
  const colorByStyle = {
    stone: 0x758275,
    awning: 0xa54b4d,
    wood: 0x8f6743,
    metal: 0x66737b,
    cloud: 0x8db9b0,
  };
  const style = platform.style ?? 'stone';
  const height = platform.height ?? 10;
  scene.add.rectangle(platform.x, platform.y + 4, platform.width + 4, height + 3, 0x06111e, 0.34).setDepth(1);
  const object = scene.add.rectangle(platform.x, platform.y, platform.width, height, colorByStyle[style]);
  object.setStrokeStyle(1.5, style === 'awning' ? 0xe0c06f : 0x233d3e, 0.95);
  object.setDepth(2);
  scene.add.rectangle(platform.x, platform.y - height / 2 + 1.5, platform.width - 5, 2, 0xf1dfb1, 0.52).setDepth(3);
  if (style === 'metal') {
    for (const x of [platform.x - platform.width / 2 + 9, platform.x + platform.width / 2 - 9]) {
      scene.add.circle(x, platform.y, 1.5, 0xd9b769, 0.8).setDepth(3);
    }
  }
  if (style === 'cloud') {
    for (let x = platform.x - platform.width / 2 + 9; x < platform.x + platform.width / 2; x += 18) {
      scene.add.circle(x, platform.y + 2, 7, colorByStyle[style], 0.8).setDepth(1.5);
    }
  }
  return object;
}
